import type {
	AtAGlanceEvidenceReference,
	AtAGlanceOutput,
	AtAGlanceStatus,
} from './atAGlanceTypes.ts';
import { adaptLegacyAtAGlanceOutput } from './adaptLegacyAtAGlanceOutput.ts';

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

const isNonEmptyString = (value: unknown): value is string =>
	typeof value === 'string' && value.trim().length > 0;

const isStatus = (value: unknown): value is AtAGlanceStatus =>
	value === 'GENERATED' || value === 'INSUFFICIENT_EVIDENCE' || value === 'UNAVAILABLE';

const parseEvidence = (value: unknown): AtAGlanceEvidenceReference[] | null => {
	if (!Array.isArray(value)) {
		return null;
	}

	const evidence: AtAGlanceEvidenceReference[] = [];
	for (const item of value) {
		if (!isRecord(item)) {
			continue;
		}
		const { id, type, entityAri, label, observedAt } = item;
		if (
			!isNonEmptyString(id) ||
			!isNonEmptyString(type) ||
			!isNonEmptyString(entityAri) ||
			!isNonEmptyString(label) ||
			!(observedAt === undefined || observedAt === null || typeof observedAt === 'string')
		) {
			continue;
		}

		evidence.push({
			id: id.trim(),
			type: type.trim(),
			entityAri: entityAri.trim(),
			label: label.trim(),
			observedAt: observedAt ?? null,
		});
	}

	return evidence;
};

/**
 * Reads the cc-smarts envelope and keeps only well-formed overview bullets and
 * per-link summaries. Returns null when the envelope itself is missing or malformed.
 */
export const parseAtAGlanceOutput = (value: unknown): AtAGlanceOutput | null => {
	if (!isRecord(value)) {
		return null;
	}

	const output = value.ai_feature_output;
	if (!isRecord(output)) {
		return null;
	}

	const { status, overview, summaries } = output;
	if (!isStatus(status)) {
		return adaptLegacyAtAGlanceOutput(output);
	}
	if (!Array.isArray(overview) || !Array.isArray(summaries)) {
		return null;
	}
	if (overview.some((item) => typeof item === 'string')) {
		return null;
	}

	const parsedOverview: AtAGlanceOutput['overview'] = [];
	for (const item of overview) {
		if (!isRecord(item)) {
			continue;
		}
		const evidence = parseEvidence(item.evidence);
		if (!isNonEmptyString(item.label) || !isNonEmptyString(item.text) || !evidence) {
			continue;
		}
		parsedOverview.push({ label: item.label.trim(), text: item.text.trim(), evidence });
	}

	const parsedSummaries: AtAGlanceOutput['summaries'] = [];
	for (const summary of summaries) {
		if (!isRecord(summary)) {
			continue;
		}
		const evidence = parseEvidence(summary.evidence);
		if (
			!isNonEmptyString(summary.ari) ||
			!isNonEmptyString(summary.title) ||
			!isNonEmptyString(summary.oneLiner) ||
			!evidence
		) {
			continue;
		}
		parsedSummaries.push({
			ari: summary.ari.trim(),
			title: summary.title.trim(),
			oneLiner: summary.oneLiner.trim(),
			evidence,
		});
	}

	return {
		status,
		overview: parsedOverview,
		summaries: parsedSummaries,
	};
};
